"use client";

import { useAppSelector } from "@/store/hooks";
import { DefaultBackground } from "@/ui";
import type React from "react";
import FooterBottom from "./FooterBottom";
import FooterContact from "./FooterContact";
import FooterExperience from "./FooterExperience";
import FooterNewsletter from "./FooterNewsletter";
import FooterQuote from "./FooterQuote";
import FooterStory from "./FooterStory";

export interface FooterProps {
  className?: string;
  showQuote?: boolean;
  showNewsletter?: boolean;
  showExperience?: boolean;
  showStory?: boolean;
  showContact?: boolean;
  showBottom?: boolean;
}

const Footer: React.FC<FooterProps> = ({
  className = "",
  showQuote = true,
  showNewsletter = true,
  showExperience = true,
  showStory = true,
  showContact = true,
  showBottom = true,
}) => {
  const { data, loading, error } = useAppSelector((state) => state.content);

  const decorativeElements = [
    {
      size: "md" as const,
      position: { top: "16%", right: "6%" },
      opacity: 0.1,
    },
    {
      size: "sm" as const,
      position: { top: "32%", left: "8%" },
      opacity: 0.15,
    },
    {
      size: "lg" as const,
      position: { bottom: "18%", right: "10%" },
      opacity: 0.12,
    },
  ];

  // Loading state
  if (loading) {
    return (
      <DefaultBackground
        decorativeElements={decorativeElements}
        positionMode="absolute"
        fullHeight={false}
        className={className}
      >
        <footer className="pb-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-12">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
        </footer>
      </DefaultBackground>
    );
  }

  // Error or no data
  if (error || !data?.footer) {
    return null;
  }

  return (
    <DefaultBackground
      decorativeElements={decorativeElements}
      positionMode="absolute"
      fullHeight={false}
      className={className}
    >
      <footer className="pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Editorial Quote Section */}
          {showQuote && data.footer.quote && (
            <FooterQuote
              quote={data.footer.quote.quote}
              author={data.footer.quote.author}
            />
          )}

          {/* Newsletter Section */}
          {showNewsletter && data.footer.newsletter && (
            <FooterNewsletter
              title={data.footer.newsletter.title}
              description={data.footer.newsletter.description}
              placeholder={data.footer.newsletter.placeholder}
              buttonText={data.footer.newsletter.buttonText}
            />
          )}

          {/* Experience Cards */}
          {showExperience && data.footer.experienceCards && (
            <FooterExperience cards={data.footer.experienceCards} />
          )}

          {/* Our Story Section */}
          {showStory && data.footer.story && (
            <FooterStory
              sectionTitle={data.footer.story.sectionTitle}
              title={data.footer.story.title}
              description={data.footer.story.description}
            />
          )}

          {/* Contact Information */}
          {showContact && data.footer.contact && (
            <FooterContact contactInfo={data.footer.contact} />
          )}

          {/* Footer Bottom */}
          {showBottom && data.footer.social && (
            <FooterBottom bottomData={data.footer.social} />
          )}
        </div>
      </footer>
    </DefaultBackground>
  );
};

export default Footer;
